import { createFileRoute } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { StatCell } from '@workspace/ui/components/stat-cell'
import { useState } from 'react'
import { AgentSessionsTable } from '../../../features/admin/components/agent-sessions-table'
import { useAdminAgentOverviewQuery } from '../../../features/admin/hooks/use-admin-api'

export const Route = createFileRoute('/admin/agent/')({
  component: AdminAgentPage,
})

const TOOLS = ['FIND', 'READ', 'CHECK', 'NOTE', 'JOURNAL', 'THESIS'] as const
type Tool = (typeof TOOLS)[number]

type Overview = NonNullable<ReturnType<typeof useAdminAgentOverviewQuery>['data']>['data']
type JobHealth = Overview['jobHealth'][number]

const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`
const formatMs = (value: number | null) => (value === null ? '-' : `${Math.round(value)} ms`)

function formatAge(value: Date | string | null) {
  if (!value) return '-'
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000))
  if (minutes < 60) return `${minutes} min`
  const hours = Math.round(minutes / 60)
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} d`
}

function AdminAgentPage() {
  const [tool, setTool] = useState<Tool | undefined>(undefined)
  // Tiles and job health only: the sessions list below loads its own pages.
  const query = useAdminAgentOverviewQuery({ tool, page: 1, limit: 1 })
  const data = query.data?.data

  const healthColumns: TableColumn<JobHealth>[] = [
    { header: 'Job', render: (item) => <CellPrimary>{item.job}</CellPrimary> },
    {
      header: 'Pending',
      width: '110px',
      render: (item) => <CellText>{item.pending}</CellText>,
    },
    {
      header: 'Failed',
      width: '110px',
      render: (item) =>
        item.failed > 0 ? (
          <Badge variant="destructive">{item.failed}</Badge>
        ) : (
          <CellText>0</CellText>
        ),
    },
    {
      header: 'Oldest pending',
      width: '150px',
      render: (item) => <CellText>{formatAge(item.oldestPendingAt ?? null)}</CellText>,
    },
  ]

  return (
    <div className="mx-auto max-w-6xl space-y-6 py-2">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">AI sessions</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          How investors' assistants use the MCP server, over the last seven days. Expand a call to
          see what the assistant sent and our reply; the reply is cleared after 90 days.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCell size="sm" value={data?.metrics.calls ?? '-'} label="Calls" />
        <StatCell
          size="sm"
          value={data ? formatMs(data.metrics.avgLatencyMs) : '-'}
          label="Avg latency"
        />
        <StatCell
          size="sm"
          value={data ? formatMs(data.metrics.p95LatencyMs) : '-'}
          label="p95 latency"
        />
        <StatCell
          size="sm"
          value={data ? formatPercent(data.metrics.errorRate) : '-'}
          label="Errors"
          accent={(data?.metrics.errorRate ?? 0) > 0.01}
        />
        <StatCell
          size="sm"
          value={data ? formatPercent(data.metrics.refusalRate) : '-'}
          label="Refused"
        />
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Engine jobs</h2>
        <DataTable
          columns={healthColumns}
          data={data?.jobHealth ?? []}
          isLoading={query.isLoading}
          emptyMessage="No job data."
          keyExtractor={(item) => item.job}
          minWidth="640px"
        />
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="mr-2 text-sm font-semibold text-foreground">Sessions</h2>
          <Button
            size="sm"
            variant={tool === undefined ? 'default' : 'outline'}
            onClick={() => {
              setTool(undefined)
            }}
          >
            All tools
          </Button>
          {TOOLS.map((value) => (
            <Button
              key={value}
              size="sm"
              variant={tool === value ? 'default' : 'outline'}
              onClick={() => {
                setTool(value)
              }}
            >
              {value.toLowerCase()}
            </Button>
          ))}
        </div>
        <AgentSessionsTable tool={tool} />
      </section>
    </div>
  )
}
